import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { getLunar, type LunarState } from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import MoonPhase from '@/components/MoonPhase';
import PageNav from '@/components/PageNav';

/**
 * Lunar Ritual screen — surfaces today's lunar phase, the matching
 * ritual prompt, and the next 60 days of new/full moons.
 *
 * The big idea: a single screen the user wants to OPEN, not a setting
 * tucked into Profile. It deepens the "esoteric" brand promise and
 * gives notifications a meaningful destination.
 */
export default function Rituals() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<LunarState | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getLunar().then(setData).catch((e) => setErr((e as Error).message));
  }, []);

  const lang = i18n.language.startsWith('ru') ? 'ru' : 'en';

  // Helper: format the gap between today and an event date.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const daysUntil = (iso: string): number => {
    const a = new Date(`${todayIso}T00:00:00Z`).getTime();
    const b = new Date(`${iso}T00:00:00Z`).getTime();
    return Math.round((b - a) / 86_400_000);
  };

  const formatDate = (iso: string): string => {
    return new Date(`${iso}T12:00:00Z`).toLocaleDateString(
      lang === 'ru' ? 'ru-RU' : 'en-US',
      { day: 'numeric', month: 'long' },
    );
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        {t('lunar.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('lunar.subtitle')}
      </p>

      {err && <p className="text-center text-rose-400">{err}</p>}

      {data && (
        <>
          {/* Current phase — visual + name + illumination */}
          <OrnateFrame className="card-glow mb-4">
            <div className="flex items-center justify-center gap-5">
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <MoonPhase size={96} />
              </motion.div>
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-gold-500/70">
                  {t('lunar.current_phase')}
                </p>
                <p className="mt-1 font-serif text-xl text-gold-200">
                  {lang === 'ru' ? data.phase.name_ru : data.phase.name_en}
                </p>
                <p className="mt-1 text-xs text-gold-300/60">
                  {t('lunar.illumination')}: {Math.round(data.phase.illumination * 100)}%
                </p>
              </div>
            </div>
          </OrnateFrame>

          {/* Today's ritual blurb */}
          <OrnateFrame className="card-glow mb-4 border-gold-500/50">
            <p className="mb-2 text-[10px] uppercase tracking-[0.25em] text-gold-500/70">
              ✦ {t('lunar.ritual_today')} ✦
            </p>
            <h2 className="mb-3 font-serif text-xl text-gold-200">
              {data.ritual.title}
            </h2>
            <p className="whitespace-pre-line font-serif italic leading-relaxed text-gold-300/80">
              {data.ritual.body}
            </p>
          </OrnateFrame>

          {/* Today's "what to do / what to avoid" — Priestess only */}
          {data.day_advice ? (
            <div className="mb-4 grid grid-cols-1 gap-3">
              <OrnateFrame className="card-glow border-emerald-400/30">
                <p className="mb-3 text-[10px] uppercase tracking-widest text-emerald-300/80">
                  ✓ {t('lunar.good_today')}
                </p>
                <ul className="space-y-1.5">
                  {data.day_advice.good.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-snug text-gold-200">
                      <span className="mt-1 text-emerald-300">✦</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </OrnateFrame>

              <OrnateFrame className="card-glow border-rose-400/30">
                <p className="mb-3 text-[10px] uppercase tracking-widest text-rose-300/80">
                  ✗ {t('lunar.avoid_today')}
                </p>
                <ul className="space-y-1.5">
                  {data.day_advice.avoid.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-snug text-gold-200">
                      <span className="mt-1 text-rose-300/80">⊘</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </OrnateFrame>
            </div>
          ) : (
            <OrnateFrame className="card-glow mb-4 border-gold-500/40">
              <p className="mb-1 text-center text-[10px] uppercase tracking-[0.25em] text-gold-500/70">
                👑 {t('lunar.locked_label')}
              </p>
              <h3 className="mb-2 text-center font-serif text-lg text-gold-200">
                {t('lunar.locked_title')}
              </h3>
              <p className="mb-4 text-center text-xs italic leading-relaxed text-gold-300/70">
                {t('lunar.locked_blurb')}
              </p>
              <Link to="/profile" className="btn-primary block w-full text-center">
                {t('lunar.locked_cta')}
              </Link>
            </OrnateFrame>
          )}

          {/* Upcoming events — chronological calendar */}
          <section>
            <h3 className="mb-3 text-center text-[10px] uppercase tracking-widest text-gold-500/60">
              {t('lunar.upcoming_title')}
            </h3>

            {data.upcoming.length === 0 ? (
              <p className="text-center text-xs italic text-gold-300/50">
                {t('lunar.upcoming_empty')}
              </p>
            ) : (
              <ul className="space-y-2">
                {data.upcoming.map((ev) => {
                  const d = daysUntil(ev.date);
                  const label =
                    d === 0 ? t('lunar.today_label')
                    : d === 1 ? t('lunar.tomorrow_label')
                    : t('lunar.in_days', { count: d });
                  const isFull = ev.type === 'full_moon';
                  return (
                    <li
                      key={`${ev.date}-${ev.type}`}
                      className={`flex items-center justify-between rounded-xl
                                  border px-3 py-2.5 transition
                                  ${d === 0
                                    ? 'border-gold-500 bg-gold-500/15'
                                    : 'border-gold-500/20 bg-night-900/40'}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{isFull ? '🌕' : '🌑'}</span>
                        <div>
                          <p className="font-serif text-sm text-gold-200">
                            {isFull ? t('lunar.type_full_moon') : t('lunar.type_new_moon')}
                          </p>
                          <p className="text-[10px] uppercase tracking-widest text-gold-500/60">
                            {formatDate(ev.date)}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs italic
                        ${d === 0 ? 'text-gold-200' : 'text-gold-300/60'}`}>
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Astrological events — the next 60 days of retrogrades,
              eclipses, solstices, equinoxes, meteor showers. */}
          {data.astro_events && data.astro_events.length > 0 && (
            <section className="mt-8">
              <h3 className="mb-3 text-center text-[10px] uppercase tracking-widest text-gold-500/60">
                {t('astro.upcoming_title')}
              </h3>
              <ul className="space-y-2">
                {data.astro_events.map((ev) => {
                  const d = daysUntil(ev.date_start);
                  const isActive = d <= 0 && (
                    !ev.date_end || daysUntil(ev.date_end) >= 0
                  );
                  const label =
                    isActive ? t('astro.active_now')
                    : d === 0 ? t('lunar.today_label')
                    : d === 1 ? t('lunar.tomorrow_label')
                    : t('lunar.in_days', { count: d });
                  const title = lang === 'ru' ? ev.title_ru : ev.title_en;
                  const body = lang === 'ru' ? ev.body_ru : ev.body_en;
                  return (
                    <li
                      key={`${ev.date_start}-${ev.title_en}`}
                      className={`rounded-xl border px-3 py-3 transition
                                  ${isActive
                                    ? 'border-gold-500 bg-gold-500/10'
                                    : 'border-gold-500/20 bg-night-900/40'}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl shrink-0">{ev.icon}</span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="font-serif text-sm leading-tight text-gold-200">
                              {title}
                            </p>
                            <span className={`shrink-0 text-[10px] uppercase tracking-widest
                              ${isActive ? 'text-gold-300' : 'text-gold-500/60'}`}>
                              {label}
                            </span>
                          </div>
                          <p className="mt-0.5 text-[10px] uppercase tracking-widest text-gold-500/50">
                            {formatDate(ev.date_start)}
                            {ev.date_end ? ` → ${formatDate(ev.date_end)}` : ''}
                            <span className="ml-2">·</span>
                            <span className="ml-2">
                              {t(`astro.cat_${ev.category}`)}
                            </span>
                          </p>
                          <p className="mt-2 text-xs italic leading-snug text-gold-300/80">
                            {body}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-center text-[10px] italic text-gold-500/40">
                {t('astro.disclaimer')}
              </p>
            </section>
          )}
        </>
      )}

      <PageNav />
    </main>
  );
}
